<script setup lang="ts">
import type { BlockRootContext, BlockRootContextPropertyResult } from '#layers/block-engine/public';
import type { CollectionCodeKey, CollectionCodeOperation, CollectionCodeSnippetOptions } from '~/utils/code-snippet/types';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useRuntimeConfig } from '#app/nuxt';
import { consumeFormBlockRootContext } from '#layers/blocks/public';
import { CREATE_MODE_ITEM_ID, hasCompositePrimaryKey } from '#layers/data-model/public';
import { consumeListingQueryBlockRootContext } from '#layers/listing/public';
import { CONNECT_ROUTE_NAME, CONTENT_ROUTE_NAME } from '#layers/spaces/public';
import { usePaneStore, useWorkspaceSchemaStore, useWorkspaceStore } from '#layers/workspace/public';
import { consumeCollectionIdBlockRootContext, consumeItemIdBlockRootContext, consumePageTypeBlockRootContext } from '~/composables/block-context/root-context-properties';
import { getCollectionPrimaryKeyFilter } from '~/utils/code-snippet/keys';
import { getCollectionCodeSnippets } from '~/utils/code-snippet/snippets';
import { getCollectionWriteDataItem } from '~/utils/code-snippet/write-data';

const workspaceStore = useWorkspaceStore();
const runtimeConfig = useRuntimeConfig();
const { t } = useI18n();
const { findCollectionById } = useWorkspaceSchemaStore();

function isMainPageContext(rootContext: BlockRootContext) {
	return !rootContext.pageDisplayMode;
}

/**
 * Selects the active publisher after drawer and modal contexts have been filtered out.
 */
function getCurrentMainPageContextValue<TValue>(results: Array<BlockRootContextPropertyResult<TValue>>): Exclude<TValue, undefined> | undefined {
	// Context publishers register in render order, so the last main-page result wins after layout swaps.
	return results.at(-1)?.value;
}

const formContextResults = consumeFormBlockRootContext({ filter: isMainPageContext });
const listingContextResults = consumeListingQueryBlockRootContext({ filter: isMainPageContext });
const pageTypeContextResults = consumePageTypeBlockRootContext({ filter: isMainPageContext });
const itemIdContextResults = consumeItemIdBlockRootContext({ filter: isMainPageContext });
const collectionIdContextResults = consumeCollectionIdBlockRootContext({ filter: isMainPageContext });

const formContext = computed(() => getCurrentMainPageContextValue(formContextResults.value));
const listingContext = computed(() => getCurrentMainPageContextValue(listingContextResults.value));

const isDetailPage = computed(() => getCurrentMainPageContextValue(pageTypeContextResults.value) === 'detail');

const rawItemCompositeKey = computed(() => {
	if (!isDetailPage.value) return;

	const itemId = formContext.value?.currentItemId ?? getCurrentMainPageContextValue(itemIdContextResults.value);

	return itemId;
});

const isCreateMode = computed(() => {
	const itemId = rawItemCompositeKey.value;
	return isDetailPage.value && (!itemId || itemId === CREATE_MODE_ITEM_ID);
});

const itemCompositeKey = computed(() => {
	if (isCreateMode.value) return;
	return rawItemCompositeKey.value;
});

const selection = computed(() => {
	if (isDetailPage.value) {
		return formContext.value?.fields;
	}

	const query = listingContext.value?.query;
	return query ? { fields: query.fields, include: query.include } : undefined;
});

const includeAllFields = ref(false);

const pageCollection = computed(() => {
	const collectionId = getCurrentMainPageContextValue(collectionIdContextResults.value);

	return collectionId ? findCollectionById(collectionId) : undefined;
});

const collection = computed(() => {
	if (isDetailPage.value) return formContext.value?.collection ?? pageCollection.value;

	return listingContext.value?.collection ?? formContext.value?.collection ?? pageCollection.value;
});

const collectionHasCompositePrimaryKey = computed(() => collection.value ? hasCompositePrimaryKey(collection.value) : false);

const apiBaseUrl = computed(() => {
	const apiPath = (runtimeConfig.public.api as string | undefined) ?? '/api';

	if (import.meta.client) {
		return new URL(apiPath, window.location.origin).toString().replace(/\/$/, '');
	}

	return apiPath;
});

const itemCodeKey = computed((): CollectionCodeKey | undefined => {
	const pk = itemCompositeKey.value;
	if (!pk) return undefined;

	const values = Object.values(pk) as Array<string | number>;

	return values.length === 1 ? values[0]! : pk;
});

const itemIdDisplay = computed(() => {
	const key = itemCodeKey.value;

	if (key === undefined) return null;
	if (typeof key === 'object') return JSON.stringify(key);

	return String(key);
});

type PanelOperation = Extract<CollectionCodeOperation, 'readMany' | 'readOne' | 'readFirst' | 'createOne' | 'updateOne' | 'deleteOne'>;

const detailReadOperation = computed<PanelOperation>(() => collectionHasCompositePrimaryKey.value ? 'readFirst' : 'readOne');

const operations = computed<PanelOperation[]>(() => {
	if (isCreateMode.value) return ['createOne'];

	if (isDetailPage.value) return [detailReadOperation.value, 'updateOne', 'deleteOne'];

	return ['readMany'];
});

function getOperationSelection(operation: PanelOperation): CollectionCodeSnippetOptions['selection'] {
	// A delete snippet returns nothing, so it omits a field selection.
	if (operation === 'deleteOne') return undefined;

	if (operation === 'readMany' && includeAllFields.value) return ['*'];

	return selection.value;
}

function getOperationFilter(operation: PanelOperation): CollectionCodeSnippetOptions['filter'] {
	if (operation === 'readMany') {
		return listingContext.value?.query?.filter;
	}

	if (operation === 'readFirst' && collection.value && itemCodeKey.value !== undefined) {
		return getCollectionPrimaryKeyFilter(collection.value, itemCodeKey.value);
	}
}

function getOperationData(operation: PanelOperation): CollectionCodeSnippetOptions['data'] {
	if (!collection.value) return undefined;

	const currentValues = formContext.value?.currentValues;

	if (operation === 'createOne') {
		return getCollectionWriteDataItem(collection.value, currentValues, {
			includePrimaryKeyFields: collectionHasCompositePrimaryKey.value,
		});
	}

	if (operation === 'updateOne') {
		return getCollectionWriteDataItem(collection.value, currentValues);
	}
}

function getOperationSnippets(operation: PanelOperation) {
	const workspaceSlug = workspaceStore.currentWorkspaceSlug;

	if (!collection.value || !workspaceSlug) return null;

	const readManyQuery = operation === 'readMany' ? listingContext.value?.query : undefined;

	return getCollectionCodeSnippets({
		collection: collection.value,
		operation,
		apiBaseUrl: apiBaseUrl.value,
		workspaceSlug,
		key: itemCodeKey.value,
		filter: getOperationFilter(operation),
		sort: readManyQuery?.sort,
		limit: readManyQuery?.limit,
		offset: readManyQuery?.offset,
		data: getOperationData(operation),
		selection: getOperationSelection(operation),
	});
}

const operationLocaleKeys: Record<PanelOperation, string> = {
	readMany: 'read_many',
	readOne: 'read_one',
	readFirst: 'read_one',
	createOne: 'create_one',
	updateOne: 'update_one',
	deleteOne: 'delete_one',
};

const documentationLinks: Record<PanelOperation, string> = {
	readMany: 'https://docs.monospace.io/en/developer/api/reading-data#get-all-items',
	readOne: 'https://docs.monospace.io/en/developer/api/reading-data#get-a-single-item-by-id',
	readFirst: 'https://docs.monospace.io/en/developer/api/reading-data#get-the-first-matching-item',
	createOne: 'https://docs.monospace.io/en/developer/api/writing-data#create-a-single-item',
	updateOne: 'https://docs.monospace.io/en/developer/api/writing-data#update-a-single-item-by-id',
	deleteOne: 'https://docs.monospace.io/en/developer/api/writing-data#delete-a-single-item-by-id',
};

interface CodePanelSection {
	operation: PanelOperation;
	localeKey: string;
	documentationLink: string;
	showAllFieldsToggle: boolean;
	snippets: { sdk: string; rest: string } | null;
}

const sections = computed<CodePanelSection[]>(() => operations.value.map((operation) => ({
	operation,
	localeKey: `code_panel.operations.${operationLocaleKeys[operation]}`,
	documentationLink: documentationLinks[operation],
	showAllFieldsToggle: operation === 'readMany',
	snippets: getOperationSnippets(operation),
})));

const operationTabs = computed(() => sections.value.map((section) => ({
	label: t(`${section.localeKey}.label`),
	value: section.operation,
	section,
})));

function operationProps(section: CodePanelSection) {
	return {
		localeKey: section.localeKey,
		documentationLink: section.documentationLink,
		showAllFieldsToggle: section.showAllFieldsToggle,
		snippets: section.snippets,
	};
}

const selectedOperation = ref<PanelOperation>();

watch(operations, (currentOperations) => {
	if (!selectedOperation.value || !currentOperations.includes(selectedOperation.value)) {
		selectedOperation.value = currentOperations[0];
	}
}, { immediate: true });

// Auto close panel

const paneStore = usePaneStore();

function closePanel() {
	paneStore.togglePanel({
		panelId: 'code',
		open: false,
	});
}

const route = useRoute();

watch(() => route.matched.some((m) => m.name === CONTENT_ROUTE_NAME), (inContentSpace) => {
	if (!inContentSpace) {
		closePanel();
	}
}, {
	immediate: true,
});
</script>

<template>
	<DPanel
		:title="$t('code_panel.title')"
		closable
		@close="closePanel"
	>
		<div class="flex min-h-0 grow flex-col gap-4 overflow-y-auto px-5 py-4">
			<p v-if="!collection" class="text-sm text-muted">
				{{ $t('code_panel.empty') }}
			</p>

			<template v-else>
				<p class="text-sm text-default">
					{{ $t('code_panel.subtitle') }}
				</p>

				<div
					v-if="isDetailPage && itemIdDisplay !== null"
					class="rounded-lg bg-elevated py-0.5 ps-3 pe-0.5"
				>
					<DFormField
						:label="$t('code_panel.item_key')"
						layout="inline"
					>
						<DInput
							:model-value="itemIdDisplay"
							readonly
							copy
							size="sm"
							block
							class="font-mono"
						/>
					</DFormField>
				</div>

				<DTabs
					v-if="operationTabs.length > 1"
					v-model="selectedOperation"
					:items="operationTabs"
					variant="link"
					:ui="{ list: '-mx-5 px-5' }"
				>
					<template #content="{ item }">
						<CodePanelOperation
							v-model:include-all-fields="includeAllFields"
							v-bind="operationProps(item.section)"
						/>
					</template>
				</DTabs>

				<CodePanelOperation
					v-else-if="sections[0]"
					v-model:include-all-fields="includeAllFields"
					v-bind="operationProps(sections[0])"
				/>
			</template>
		</div>

		<template #footer>
			<div class="flex items-center gap-3 border-t px-5 py-4">
				<span class="flex size-9 shrink-0 items-center justify-center rounded-lg bg-elevated">
					<DIcon name="lucide:plug" class="size-5" />
				</span>
				<DTextLink
					:to="{ name: CONNECT_ROUTE_NAME }"
					:text="$t('code_panel.connect_hint')"
					class="text-sm"
				/>
			</div>
		</template>
	</DPanel>
</template>
